import { create } from "zustand";
import $api from "../shared/api/axios";
import { TChapter, TGetProjectResponse } from "../shared/types/projects";
import { tryCatch } from "../shared/lib/tryCatch";
import { toast } from "sonner";

export type TPresentationStatus = "generating" | "completed" | "failed" | null;

export type TNextStep = {
  id: string;
  summary?: string;
  text: string;
  completed: boolean;
};

export type TProject = {
  id: string;
  name: string;
  organizationId?: number;
  shortDescription: string | null;
  logoUrl: string | null;
  projectTemplateSlug: string;
  projectTemplateName: string;
  chatEnabled: boolean;
  decisionContext?: string | null;
  successMetrics?: string | null;
  targetAudience?: string | null;
  additionalContext?: string | null;
  language: string;
  decisionStatus?: string | null;
  nextSteps?: TNextStep[];

  projectChapters?: TChapter[];
  projectInstructions?: string | null;
  notifications?: Record<number, boolean>;
  presentation?: {
    status: TPresentationStatus;
    gammaWebUrl: string | null;
    gammaPptxUrl: string | null;
    slidesCount: number;
  } | null;
};

type TProjectInRequest = {
  name: string;
  projectTemplateSlug?: string;
  description?: string;
  projectInstructions?: string;
  language?: string;
  chaptersInstructions: Record<string, string> | null;
  logoBase64?: string;
  decisionContext?: string;
  successMetrics?: string;
  targetAudience?: string;
  additionalContext?: string;
  documentIds?: number[];
  presentationPrompt?: string;
};

type TOptionalProjectInRequest = Partial<Omit<TProjectInRequest, "projectTemplateId">>;

type TProjectState = {
  projects: TProject[];
  isLoading: boolean;
  selectedProject: TProject | null;
  isFetchProjectLoading: boolean;

  fetchSelectedProject: (id: string, noLoadingState?: boolean) => Promise<TGetProjectResponse | null | undefined>;
  setProjects: (fetchedProjects: TProject[]) => void;
  setSelectedProject: (project: TProject | null) => void;
  updateSelectedProjectPresentation: (presentation: TProject["presentation"]) => void;
  updateDecisionStatus: (projectId: string, decisionStatus: string, abortController?: AbortController) => Promise<void>;
  updateNextSteps: (projectId: string, nextSteps: TNextStep[], abortController?: AbortController) => Promise<void>;
  regenerateNextSteps: (projectId: string) => Promise<TNextStep[]>;
  regenerateDecisionStatus: (projectId: string) => Promise<string>;
  addProject: (project: TProjectInRequest) => Promise<string>;
  updateProject: (id: string, project: TOptionalProjectInRequest, abortController?: AbortController) => Promise<void>;
  archiveProject: (id: string) => Promise<void>;
  duplicateProject: (id: string, organizationId?: number) => Promise<void>;
  updateChapterNotification: (projectId: string, chapterId: number, hasNotification: boolean) => void;
  getChapterNotification: (projectId: string, chapterId: number) => boolean;
};

export const useProjectStore = create<TProjectState>((set, get) => ({
  projects: [],
  isLoading: false,
  selectedProject: null,
  isFetchProjectLoading: false,

  setProjects: (fetchedProjects: TProject[]) => {
    set({ projects: fetchedProjects, isLoading: false });
  },

  setSelectedProject: (project: TProject | null) => {
    set({ selectedProject: project });
  },

  updateSelectedProjectPresentation: (presentation: TProject["presentation"]) => {
    const { selectedProject, projects } = get();
    if (!selectedProject) return;

    const updatedProject = { ...selectedProject, presentation };
    set({ selectedProject: updatedProject });

    const updatedProjects = projects.map((project) => (project.id === selectedProject.id ? updatedProject : project));
    set({ projects: updatedProjects });
  },

  updateDecisionStatus: async (projectId: string, decisionStatus: string, abortController?: AbortController) => {
    await $api.patch(
      `/projects/${projectId}/decision-status`,
      { decisionStatus },
      {
        signal: abortController?.signal
      }
    );

    if (abortController?.signal.aborted) return;
  },

  updateNextSteps: async (projectId: string, nextSteps: TNextStep[], abortController?: AbortController) => {
    await $api.patch(
      `/projects/${projectId}/next-steps`,
      { nextSteps },
      {
        signal: abortController?.signal
      }
    );

    if (abortController?.signal.aborted) return;
  },

  regenerateNextSteps: async (projectId: string) => {
    const { selectedProject, projects } = get();
    const response = await $api.post<{ data: { nextSteps: TNextStep[] } }>(
      `/projects/${projectId}/next-steps/regenerate`,
      {}
    );
    const nextSteps = response.data.data.nextSteps;

    if (selectedProject?.id === projectId) {
      set({ selectedProject: { ...selectedProject, nextSteps } });
    }

    const updatedProjects = projects.map((project) => (project.id === projectId ? { ...project, nextSteps } : project));
    set({ projects: updatedProjects });

    return nextSteps;
  },

  regenerateDecisionStatus: async (projectId: string) => {
    const { selectedProject, projects } = get();
    const response = await $api.post<{ data: { decisionStatus: string } }>(
      `/projects/${projectId}/decision-status/regenerate`,
      {}
    );
    const decisionStatus = response.data.data.decisionStatus;

    if (selectedProject?.id === projectId) {
      set({ selectedProject: { ...selectedProject, decisionStatus } });
    }

    const updatedProjects = projects.map((project) =>
      project.id === projectId ? { ...project, decisionStatus } : project
    );
    set({ projects: updatedProjects });

    return decisionStatus;
  },

  fetchSelectedProject: async (projectId: string, noLoadingState?: boolean) => {
    if (get().isFetchProjectLoading) return null;
    if (!noLoadingState) set({ isFetchProjectLoading: true });
    const [response, error] = await tryCatch($api.get<{ data: TGetProjectResponse }>(`/projects/${projectId}`));
    set({ isFetchProjectLoading: false });

    if (error) {
      set({ selectedProject: null });
      toast.error("Failed to fetch project details");
      console.error("Error fetching project:", error);
      return null;
    }

    const projectData = response.data.data;
    set({
      selectedProject: {
        ...projectData,
        id: String(projectData.id),
        language: projectData.language || "english",
        decisionStatus: projectData.decisionStatus,
        nextSteps: projectData.nextSteps || []
      }
    });
    return projectData;
  },

  addProject: async ({
    name,
    description,
    logoBase64,
    projectTemplateSlug,
    chaptersInstructions,
    projectInstructions,
    language,
    decisionContext,
    successMetrics,
    targetAudience,
    additionalContext,
    documentIds,
    presentationPrompt
  }: TProjectInRequest) => {
    set({ isLoading: true });
    const response = await $api.post("/projects", {
      name,
      shortDescription: description,
      logoBase64,
      projectTemplateSlug,
      chaptersInstructions,
      projectInstructions,
      language,
      decisionContext,
      successMetrics,
      targetAudience,
      additionalContext,
      documentIds,
      presentationPrompt
    });
    const { projects } = get();
    const resProject = response.data.data;
    const newProject: TProject = { ...resProject, id: String(resProject.id) };
    set({ projects: [...projects, newProject], isLoading: false });
    return newProject.id;
  },

  updateProject: async (id, { description, ...data }, abortController?: AbortController) => {
    set({ isLoading: true });
    const response = await $api.patch<{ data: TGetProjectResponse }>(
      `/projects/${id}`,
      {
        ...data,
        shortDescription: description
      },
      {
        signal: abortController?.signal
      }
    );
    const { projects, selectedProject } = get();
    const resProject = response.data.data;
    const newProject: TProject = {
      ...resProject,
      projectChapters: resProject.projectChapters || selectedProject?.projectChapters,
      id: String(resProject.id),
      shortDescription: resProject.shortDescription || "",
      logoUrl: resProject.logoUrl || "",
      language: resProject.language || "english",
      chatEnabled: true
    };
    const updatedProjects = projects.map((project) => (project.id === id ? newProject : project));
    if (abortController?.signal.aborted) {
      return;
    }
    if (selectedProject?.id === id) {
      set({ selectedProject: newProject });
    }
    set({ projects: updatedProjects, isLoading: false });
  },

  archiveProject: async (id: string) => {
    set({ isLoading: true });
    await $api.delete(`/projects/${id}`);
    set({ projects: get().projects.filter((project) => project.id !== id), isLoading: false });
  },

  duplicateProject: async (id: string, organizationId?: number) => {
    set({ isLoading: true });
    const { projects } = get();
    const response = await $api.post(`/projects/${id}/duplicate`, {
      organizationId
    });
    const resProject = response.data.data;
    const newProject: TProject = {
      id: String(resProject.id),
      name: resProject.name,
      projectTemplateSlug: resProject.projectTemplateSlug,
      projectTemplateName: resProject.projectTemplateName,
      shortDescription: resProject.shortDescription || "",
      logoUrl: resProject.logoUrl || "",
      chatEnabled: true,
      decisionContext: resProject.decisionContext,
      successMetrics: resProject.successMetrics,
      targetAudience: resProject.targetAudience,
      additionalContext: resProject.additionalContext,
      language: resProject.language,
      projectInstructions: resProject.projectInstructions
    };
    set({ projects: [newProject, ...projects], isLoading: false });
  },

  updateChapterNotification: (projectId: string, chapterId: number, hasNotification: boolean) => {
    const { projects, selectedProject } = get();

    if (selectedProject?.id === projectId) {
      const notifications = { ...selectedProject.notifications, [chapterId]: hasNotification };
      set({ selectedProject: { ...selectedProject, notifications } });
    }

    set({
      projects: projects.map((project) => {
        if (project.id === projectId) {
          const notifications = { ...project.notifications, [chapterId]: hasNotification };
          return { ...project, notifications };
        }
        return project;
      })
    });
  },

  getChapterNotification: (projectId: string, chapterId: number) => {
    const { selectedProject } = get();
    if (selectedProject?.id === projectId) {
      return selectedProject.notifications?.[chapterId] ?? false;
    }
    return false;
  }
}));
